import { Fragment, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useLanguage } from "../../context/Language/LanguageContext";
import { buildCatapultJumpStats } from "../utils/CatapultJumpStatsObject";
import {
    getJumpHeightUnit,
    jumpHeightInUnit,
    kSkillBlock,
    kSkillServe,
    kSkillSet,
    kSkillSettersCall,
    kSkillSpike,
} from "../utils/Constants";

const kJumpLikeSkills = new Set([
  kSkillServe,
  kSkillSet,
  kSkillSpike,
  kSkillBlock,
  kSkillSettersCall,
]);
// Generous pad around a set's first/last event time when windowing this
// player's raw Catapult jumps into that set -- same idea as vert-sync's
// `vpobj.threshold` pad in VertStatsReport.jsx.
const kWindowPaddingSec = 3.0;

function eventsForShirt(events, shirtNumber) {
  return (events ?? []).filter(
    (ev) =>
      Number(ev.Player?.shirtNumber ?? ev.Player?.ShirtNumber) === shirtNumber &&
      kJumpLikeSkills.has(ev.EventType)
  );
}

// Per-player jump report (Spike/Block/Serve/Set breakdowns, All Jumps vs All
// Action Jumps, per set + match totals) -- based on the sibling vert-sync
// project's VertStatsReport.jsx, adapted for Catapult Focus data: vert-sync's
// Landing Impact rows (G-force on landing, a Vert-only metric) are replaced
// with "Hang Time" (ev.imaDurationSec, the jump's airtime).
function CatapultJumpStatsReport({ match, player, shirtNumber, lastName, jumps, offset, onClose }) {
  const { t } = useLanguage();
  const reportRef = useRef();
  const unit = getJumpHeightUnit();
  const unitLabel = unit === "in" ? "in" : "cm";
  const playerLabel = player ? player.NickName || player.LastName : lastName;

  const saveToPDF = useReactToPrint({
    content: () => reportRef.current,
    contentRef: reportRef,
    documentTitle: `Jump Report - ${playerLabel} - ${match.teamA?.Name} vs ${match.teamB?.Name}`,
    pageStyle: `
      @page { size: portrait; margin: 2cm 1.5cm; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
      thead { display: table-header-group; }
    `,
  });

  const columns = useMemo(() => {
    const cols = [];
    for (const set of match.sets ?? []) {
      if (!set.events?.length) continue;
      const first = set.events[0];
      const last = set.events[set.events.length - 1];
      if (!first.TimeStamp || !last.TimeStamp) continue;
      const startSec = first.TimeStamp.getTime() / 1000 - kWindowPaddingSec;
      const endSec = last.TimeStamp.getTime() / 1000 + kWindowPaddingSec;
      const actionEvents = eventsForShirt(set.events, shirtNumber);
      const jumpsInSet =
        offset == null
          ? []
          : (jumps ?? []).filter((j) => {
              if (j.eventSec == null) return false;
              const matchSec = j.eventSec - offset;
              return matchSec >= startSec && matchSec <= endSec;
            });
      cols.push({
        label: `${t("Set")} ${set.GameNumber}`,
        stats: buildCatapultJumpStats(actionEvents, jumpsInSet),
      });
    }
    const matchActionEvents = eventsForShirt(match.events, shirtNumber);
    cols.push({ label: t("Match"), stats: buildCatapultJumpStats(matchActionEvents, jumps ?? []) });
    return cols;
  }, [match, shirtNumber, jumps, offset, t]);

  const fmt = (cm) => (cm ? `${jumpHeightInUnit(cm, unit).toFixed(1)} ${unitLabel}` : "-");
  const avgStr = (total, count) =>
    count ? `${jumpHeightInUnit(total / count, unit).toFixed(1)} ${unitLabel} (${count})` : "-";
  const hangTimeStr = (total, count) => (count ? `${(total / count).toFixed(2)}s (${count})` : "-");

  const row = (label, render, i) => (
    <tr key={label} className={i % 2 === 1 ? "bg-gray-50" : ""}>
      <td className="whitespace-nowrap py-1 px-2 border-b border-gray-200">{t(label)}</td>
      {columns.map((c, ci) => (
        <td key={ci} className="py-1 px-2 border-b border-gray-200 text-center tabular-nums">
          {render(c.stats)}
        </td>
      ))}
    </tr>
  );

  const sectionHeader = (label) => (
    <tr key={label}>
      <th
        colSpan={columns.length + 1}
        className="bg-gray-700 text-white text-left px-2 py-1 text-xs font-semibold tracking-wide"
      >
        {t(label)}
      </th>
    </tr>
  );

  // (label, render-fn) pairs per section -- kept as data so the alternating
  // row shading below can be computed from a flat index instead of by hand.
  const sections = [
    {
      title: "SPIKING",
      rows: [
        ["Spike Jump Average", (s) => avgStr(s.spikeJumpTotal, s.spikeCountTotal)],
        ["Spike Kill Jump Average", (s) => avgStr(s.spikeJumpKillTotal, s.spikeCountKillTotal)],
        ["Spike Error Jump Average", (s) => avgStr(s.spikeJumpErrorTotal, s.spikeCountErrorTotal)],
        ["Spike Blocked Jump Average", (s) => avgStr(s.spikeJumpBlockedTotal, s.spikeCountBlockedTotal)],
        ["Spike Jump Max / Std. Dev.", (s) => `${fmt(s.spikeJumpMaximum)} / ${fmt(s.spikeJumpStandardDeviation)}`],
        ["Spike Hang Time", (s) => hangTimeStr(s.spikeHangTimeTotal, s.spikeHangTimeCountTotal)],
      ],
    },
    {
      title: "BLOCKING",
      rows: [
        ["Block Jump Average", (s) => avgStr(s.blockJumpTotal, s.blockCountTotal)],
        ["Block Kill Jump Average", (s) => avgStr(s.blockJumpKillTotal, s.blockCountKillTotal)],
        ["Block Jump Max / Std. Dev.", (s) => `${fmt(s.blockJumpMaximum)} / ${fmt(s.blockJumpStandardDeviation)}`],
        ["Block Hang Time", (s) => hangTimeStr(s.blockHangTimeTotal, s.blockHangTimeCountTotal)],
      ],
    },
    {
      title: "SERVING",
      rows: [
        ["Serve Jump Average", (s) => avgStr(s.serveJumpTotal, s.serveCountTotal)],
        ["Serve Ace Jump Average", (s) => avgStr(s.serveJumpKillTotal, s.serveCountKillTotal)],
        ["Serve Error Jump Average", (s) => avgStr(s.serveJumpErrorTotal, s.serveCountErrorTotal)],
        ["Serve Jump Max / Std. Dev.", (s) => `${fmt(s.serveJumpMaximum)} / ${fmt(s.serveJumpStandardDeviation)}`],
        ["Serve Hang Time", (s) => hangTimeStr(s.serveHangTimeTotal, s.serveHangTimeCountTotal)],
      ],
    },
    {
      title: "SETTING",
      rows: [
        ["Set Jump Max / Std. Dev.", (s) => `${fmt(s.setJumpMaximum)} / ${fmt(s.setJumpStandardDeviation)}`],
        [
          "Standing Set / Jump Set",
          (s) => (s.setCountTotal === 0 ? "" : `${s.setCountTotal - s.setJumpCountTotal} / ${s.setJumpCountTotal}`),
        ],
        ["Set Hang Time", (s) => hangTimeStr(s.setHangTimeTotal, s.setHangTimeCountTotal)],
      ],
    },
    {
      title: "ACTION JUMPS",
      rows: [
        ["All Action Jump Average", (s) => avgStr(s.allJumpActionTotal, s.allJumpActionCountTotal)],
        ["All Action Jump Maximum", (s) => fmt(s.allJumpActionMax)],
      ],
    },
    {
      title: "ALL JUMPS",
      rows: [
        ["All Jump Average", (s) => avgStr(s.allJumpTotal, s.allJumpCountTotal)],
        ["All Jump Maximum", (s) => fmt(s.allJumpMax)],
      ],
    },
  ];

  const matchDate = match.TrainingDate ? new Date(match.TrainingDate) : null;

  return (
    <div className="modal modal-open z-[9999]" onClick={onClose}>
      <div className="modal-box max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2 gap-2">
          <h3 className="font-bold text-lg truncate">
            {t("Jump Report")} — {shirtNumber != null ? `#${shirtNumber} ` : ""}
            {playerLabel}
          </h3>
          <div className="flex gap-2 shrink-0">
            <button type="button" className="btn btn-sm btn-primary" onClick={saveToPDF}>
              {t("Save PDF")}
            </button>
            <button type="button" className="btn btn-sm" onClick={onClose}>
              {t("Close")}
            </button>
          </div>
        </div>
        {/* Styled as a plain white "paper" preview (not the app's live theme)
            so what's shown here matches what actually gets printed/saved. */}
        <div className="overflow-x-auto rounded-md shadow-sm">
          <div ref={reportRef} className="bg-white text-black p-4">
            <div className="flex items-start justify-between gap-4 border-b-2 border-gray-700 pb-2 mb-3">
              <div>
                <p className="text-xl font-bold">
                  {shirtNumber != null ? `#${shirtNumber} ` : ""}
                  {playerLabel}
                </p>
                <p className="text-sm text-gray-600">
                  {match.teamA?.Name} {t("vs")} {match.teamB?.Name}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-base font-semibold">{t("Jump Report")}</p>
                {matchDate && (
                  <p className="text-xs text-gray-600">{matchDate.toLocaleDateString()}</p>
                )}
              </div>
            </div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 border-b-2 border-gray-700"></th>
                  {columns.map((c) => (
                    <th key={c.label} className="px-2 py-1 border-b-2 border-gray-700 text-center">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => (
                  <Fragment key={section.title}>
                    {sectionHeader(section.title)}
                    {section.rows.map(([label, render], i) => row(label, render, i))}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 mt-3 text-right">
              {t("Generated by VBLive")} · {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CatapultJumpStatsReport;
